import { useTranslations } from 'next-intl';

import { formatDuration } from '@cineamo/ui/src/helpers/showing-helper';

import { CachedContentData } from '../hooks/content/useCachedContentData';

/**
 * The meta label row under a content title — release year, running time, genres, age rating.
 *
 * The cached-data counterpart of `getShowingInfo` in `@cineamo/ui`: that one takes a `ContentDto`
 * plus a `MovieDto` and derives the year and runtime itself, which is exactly what a surface built
 * on `CachedContentData` must not do — those values are already resolved for the page locale. Lives
 * in `composables` rather than `ui` because `CachedContentData` does, and `ui` sits below it.
 *
 * For a rendered title + meta row, prefer the `ContentTitle` component; this is for the surfaces
 * that need the labels as plain strings (a `DotSeparatedLabel`, a tracking payload).
 */
export function getCachedContentInfo(options: {
    content?: CachedContentData;
    t: ReturnType<typeof useTranslations>;
    showMovieDuration?: boolean;
    showGenres?: boolean;
    ageRatingText?: string;
}): string[] {
    const { content, t } = options;
    const runtimeMinutes = content?.runtime;
    const runtimeFormatted = runtimeMinutes ? formatDuration(runtimeMinutes, t) : undefined;
    const genres = options.showGenres ? content?.genres : [];

    return [
        content?.releaseYear,
        options.showMovieDuration ? runtimeFormatted : undefined,
        (genres?.slice(0, 3) || []).map((genre) => t(`genres.tmdb-genre-${genre.id}`)).join(', '),
        options.ageRatingText
    ].filter((label): label is string => !!label);
}
